





















































import { ref, onMounted, computed } from '@vue/composition-api';
import { getMfa, enableMfa, disableMfa } from '@/api/Secure';
import { errorToastMessage } from '@/helpers/errorToastMessage';

const useMfa = () => {
  const mfaStore = ref<{
    isSuccess: boolean;
    isFetching: boolean;
    error: Error | null;
    isError: boolean;
    data: {
      isMfaEnabled: boolean;
      mfaValue: string | null;
      mfaQrCode: string | null;
    } | null;
  }>({
    isSuccess: false,
    isFetching: false,
    error: null,
    isError: false,
    data: null
  });

  const isMfaSuccess = computed<boolean>(() => {
    return mfaStore.value.isSuccess;
  });

  const isMfaLoading = computed<boolean>(() => {
    return !mfaStore.value.isSuccess;
  });

  const mfaError = computed<Error | null>(() => {
    return mfaStore.value.error;
  });

  const isMfaError = computed<boolean>(() => {
    return mfaStore.value.isError;
  });

  const isMfaEnabled = computed<boolean>(() => {
    return mfaStore.value.data?.isMfaEnabled ?? false;
  });

  const mfaValue = computed<string | null>(() => {
    return mfaStore.value.data?.mfaValue ?? null;
  });

  const mfaQrCode = computed<string | null>(() => {
    return mfaStore.value.data?.mfaQrCode ?? null;
  });

  const loadMfa = async () => {
    if (mfaStore.value.isFetching) {
      return;
    }

    mfaStore.value.isFetching = true;
    try {
      mfaStore.value.data = await getMfa();
      mfaStore.value.isSuccess = true;
    } catch (error) {
      mfaStore.value.data = null;
      mfaStore.value.error =
        error instanceof Error ? error : new Error(String(error));
      mfaStore.value.isError = true;
    } finally {
      mfaStore.value.isFetching = false;
    }
  };

  const invalidateMfa = () => {
    loadMfa();
  };

  onMounted(async () => {
    await loadMfa();
  });

  return {
    isMfaSuccess,
    isMfaLoading,
    mfaError,
    isMfaError,
    isMfaEnabled,
    mfaValue,
    mfaQrCode,
    invalidateMfa
  };
};

export default {
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  setup(props: Record<string, unknown>, context): Record<string, unknown> {
    const toast = context.root.$toast;

    const mfaCode = ref<string>('');
    const isMfaUpdating = ref<boolean>(false);

    const {
      isMfaSuccess,
      isMfaError,
      isMfaEnabled,
      mfaValue,
      mfaQrCode,
      invalidateMfa
    } = useMfa();

    const isMfaCodeValid = computed<boolean>(() => {
      return mfaCode.value.length === 6;
    });

    const isDisableMfaButtonDisabled = computed<boolean>(() => {
      return !isMfaCodeValid.value;
    });

    const isEnableMfaButtonDisabled = computed<boolean>(() => {
      return !isMfaCodeValid.value;
    });

    const resetMfaCode = () => {
      mfaCode.value = '';
    };

    const handleClickDisableMfaButton = async () => {
      if (isMfaUpdating.value) {
        return;
      }

      isMfaUpdating.value = true;
      try {
        await disableMfa(mfaCode.value);
        toast.success('MFA turned off');
        resetMfaCode();
        invalidateMfa();
      } catch (error) {
        errorToastMessage(error);
      } finally {
        isMfaUpdating.value = false;
      }
    };

    const handleClickEnableMfaButton = async () => {
      if (isMfaUpdating.value) {
        return;
      }

      isMfaUpdating.value = true;
      try {
        await enableMfa(mfaCode.value);
        toast.success('MFA turned on');
        resetMfaCode();
        invalidateMfa();
      } catch (error) {
        errorToastMessage(error);
      } finally {
        isMfaUpdating.value = false;
      }
    };

    return {
      mfaCode,
      isMfaSuccess,
      isMfaError,
      isMfaEnabled,
      mfaValue,
      mfaQrCode,
      isDisableMfaButtonDisabled,
      isEnableMfaButtonDisabled,
      handleClickDisableMfaButton,
      handleClickEnableMfaButton
    };
  }
};
